import React from 'react';
import { Gift, ExternalLink } from 'lucide-react';

/**
 * AppliedDiscountBanner - Shows when an invoice has a discount applied from a previous early payment
 * @param {Object} invoice - The invoice object with discount info
 * @param {string} viewType - 'receiver' or 'sender'
 * @param {Function} onViewOriginalInvoice - Callback to view the original invoice that earned the discount
 */
const AppliedDiscountBanner = ({ 
  invoice, 
  viewType = 'receiver',
  onViewOriginalInvoice 
}) => {
  // Only show if invoice has a discount applied
  if (!invoice?.earlyPaymentDiscountApplied || !invoice?.discountAmount || invoice.discountAmount <= 0) {
    return null;
  }

  const discountAmount = parseFloat(invoice.discountAmount).toFixed(2);
  const originalInvoiceNumber = invoice.earlyPaymentOriginalInvoice || 'previous invoice';
  
  // Hide banner if invoice is paid (show small badge instead)
  if (invoice.status === 'paid') {
    return (
      <div className="inline-flex items-center space-x-2 px-4 py-2 bg-purple-100 rounded-full text-sm mb-4">
        <Gift className="w-4 h-4 text-purple-600" />
        <span className="text-purple-700 font-medium">
          Saved £{discountAmount} (Early payment reward)
        </span>
      </div>
    );
  }

  // Full banner for unpaid invoices
  return (
    <div className="rounded-xl border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50 p-6 mb-6 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-3 flex-1">
          <div className="p-3 rounded-full bg-purple-100 border-2 border-purple-300">
            <Gift className="w-8 h-8 text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-purple-900">
              ✨ DISCOUNT APPLIED: -£{discountAmount}
            </h3>
            <p className="text-sm text-purple-700 mt-1">
              {viewType === 'receiver' 
                ? `You're saving £${discountAmount} on this invoice! This is your reward for paying ${originalInvoiceNumber} early.`
                : `Customer earned £${discountAmount} discount by paying ${originalInvoiceNumber} early.`
              }
            </p>
            {onViewOriginalInvoice && (
              <button
                onClick={onViewOriginalInvoice}
                className="inline-flex items-center space-x-1 text-xs text-purple-600 hover:text-purple-800 font-medium mt-2"
              >
                <span>View original invoice</span>
                <ExternalLink className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-3xl font-bold text-purple-600">
            -£{discountAmount}
          </div>
          <div className="text-xs text-purple-600 font-medium">
            Early Payment Reward
          </div>
        </div>
      </div>

      {/* Show benefit breakdown for receiver */}
      {viewType === 'receiver' && (
        <div className="mt-4 pt-4 border-t border-purple-200">
          <div className="flex items-center justify-between text-sm">
            <span className="text-purple-700">Original Amount:</span>
            <span className="text-purple-900 font-medium">
              £{(parseFloat(invoice.total) + parseFloat(invoice.discountAmount)).toFixed(2)}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm mt-1">
            <span className="text-purple-700 font-semibold">Your Discount:</span>
            <span className="text-purple-600 font-bold">-£{discountAmount}</span>
          </div>
          <div className="flex items-center justify-between text-lg mt-2 pt-2 border-t border-purple-200">
            <span className="text-purple-900 font-bold">Amount to Pay:</span>
            <span className="text-purple-900 font-bold">
              £{parseFloat(invoice.total).toFixed(2)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppliedDiscountBanner;

